/**
 * Normalize basket membership for Settings UI.
 * Supports API shapes:
 * - clients: [{ name, broker_client_code, multiplier, client_id }]  (GET /baskets/)
 * - basket_clients: [{ id, client_id, broker_client_code, multiplier }]
 * - broker_client_codes: string[]
 */
export function enrichBasketMembers(basket, allClients) {
  if (!basket) return [];

  const clients = Array.isArray(allClients) ? allClients : [];
  const byId = new Map(clients.map((c) => [Number(c.id), c]));
  const byCode = new Map(
    clients.map((c) => [String(c.broker_client_code ?? "").trim().toUpperCase(), c])
  );

  const resolveFromClient = (fromClient, codeRaw, nameRaw, multRaw, basketClientId) => {
    const broker_client_code =
      fromClient?.broker_client_code ??
      (codeRaw != null && String(codeRaw).trim() !== "" ? String(codeRaw).trim() : "—");
    const resolvedClientId =
      fromClient?.id != null && !Number.isNaN(Number(fromClient.id))
        ? Number(fromClient.id)
        : null;
    const multiplierNum = multRaw != null && multRaw !== "" ? Number(multRaw) : null;
    return {
      basketClientId: basketClientId != null ? String(basketClientId) : null,
      clientId: resolvedClientId,
      broker_client_code,
      full_name:
        (nameRaw != null && String(nameRaw).trim() !== "" ? String(nameRaw).trim() : "") ||
        fromClient?.full_name ||
        fromClient?.name ||
        "",
      multiplier:
        multiplierNum != null && Number.isFinite(multiplierNum) && multiplierNum >= 0
          ? multiplierNum
          : 1,
      canRemove: resolvedClientId != null,
    };
  };

  // Current API: basket.clients[{ name, broker_client_code, multiplier, client_id }]
  const apiClients = basket.clients;
  if (Array.isArray(apiClients)) {
    return apiClients.map((row) => {
      const apiClientId =
        row?.client_id != null && !Number.isNaN(Number(row.client_id))
          ? Number(row.client_id)
          : null;
      const codeRaw = row?.broker_client_code;
      const codeKey = codeRaw != null ? String(codeRaw).trim().toUpperCase() : "";
      const fromClient =
        (apiClientId != null ? byId.get(apiClientId) : null) ||
        (codeKey ? byCode.get(codeKey) : null);
      const resolved = resolveFromClient(
        fromClient,
        codeRaw,
        row?.name ?? row?.full_name,
        row?.multiplier ?? row?.client_multiplier,
        row?.id ?? row?.basket_client_id ?? null
      );
      // Prefer client_id from basket API for remove-basket-clients
      if (apiClientId != null) {
        return { ...resolved, clientId: apiClientId, canRemove: true };
      }
      return resolved;
    });
  }

  const links = basket.basket_clients;
  if (Array.isArray(links) && links.length > 0) {
    return links.map((row) => {
      const clientId = row.client_id != null ? Number(row.client_id) : null;
      const basketClientId = row.id ?? row.basket_client_id ?? null;
      const codeRaw = row.broker_client_code;
      const codeKey = codeRaw != null ? String(codeRaw).trim().toUpperCase() : "";
      const fromClient =
        (clientId != null && !Number.isNaN(clientId) ? byId.get(clientId) : null) ||
        (codeKey ? byCode.get(codeKey) : null);
      const resolved = resolveFromClient(
        fromClient,
        codeRaw,
        row?.name ?? row?.full_name,
        row.multiplier ?? row.client_multiplier,
        basketClientId
      );
      if (
        resolved.clientId == null &&
        clientId != null &&
        !Number.isNaN(clientId)
      ) {
        return { ...resolved, clientId, canRemove: true };
      }
      if (clientId != null && !Number.isNaN(clientId)) {
        return { ...resolved, clientId, canRemove: true };
      }
      return resolved;
    });
  }

  const codes = basket.broker_client_codes || [];
  return codes.map((code) => {
    const codeKey = String(code).trim().toUpperCase();
    const fromClient = byCode.get(codeKey);
    return resolveFromClient(fromClient, code, null, 1, null);
  });
}

export function memberClientIdSet(members) {
  const s = new Set();
  (members || []).forEach((m) => {
    if (m.clientId != null && !Number.isNaN(m.clientId)) s.add(m.clientId);
  });
  return s;
}
